import React from 'react';

import { faBuilding, faExclamationTriangle, faSignIn, faSignOut } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Tooltip, TooltipPlacement } from '@shared/components/bootstrap/tooltip';
import { formatBuildingRestrictionRange } from '@shared/utils/building_restriction_time';

import {
  DispatcherEstimationFormModal,
  type RestOrderPayload,
} from '@admin/components/dispatcher/estimation_form_modal';
import { MoverCountIcon as DispatcherMoverCountIcon } from '@admin/components/dispatcher/mover_count_icon';
import { formatDateTz } from '@admin/components/shifts/shift_entry_timeline';
import { orderURL } from '@admin/config/routes';
import { getPrimaryCustomer } from '@admin/utils/account_customers';

import { DISPATCHER_CONSTANTS } from './dispatcher_constants';
import { orderDispatchError } from './dispatcher_order_errors';
import { useDispatcherOrderHoverOverlay } from './dispatcher_order_hover_overlay';
import { DispatcherOrderRecord, DispatcherDispatchRecord } from './dispatcher_types';

type DispatcherOrderPreviewProps = {
  order: DispatcherOrderRecord;
  dispatch?: DispatcherDispatchRecord | null;
  mode?: 'viewing' | 'modifying';
  undispatched?: boolean;
  onDragstart?: (order: DispatcherOrderRecord) => void;
  onDragmove?: (order: DispatcherOrderRecord, event: DragEvent) => void;
  onDragend?: (order: DispatcherOrderRecord) => void;
};

const orderSizingClass = (
  order: DispatcherOrderRecord,
  dispatch?: DispatcherDispatchRecord | null,
  undispatched = false,
) => {
  if (!dispatch || undispatched) {
    return 'dispatcher-order-default';
  }

  if (order.estimated_duration && order.estimated_duration > 3600) {
    return 'dispatcher-order-expanded';
  }

  return 'dispatcher-order-collapsed';
};

const orderTypeClass = (order: DispatcherOrderRecord) => {
  if (order.moving) {
    return order.long_distance_moving ? 'dispatcher-order-long-distance-moving' : 'dispatcher-order-moving';
  }

  return `dispatcher-order-${order.type ?? 'unknown'}`;
};

const isWarehouseOrder = (order: DispatcherOrderRecord) =>
  order.address?.addressable_type === 'Warehouse' && order.type !== 'restock' && order.service_type !== 'drop_off';

const hasBuildingRestrictions = (order: DispatcherOrderRecord) => order.building_restrictions_answer === 'yes';

const buildingRestrictionsTimes = (order: DispatcherOrderRecord) => {
  if (!hasBuildingRestrictions(order)) {
    return undefined;
  }

  return formatBuildingRestrictionRange(order.building_opening_hour, order.building_closing_hour);
};

const buildingRestrictionsTitle = (order: DispatcherOrderRecord) => {
  const times = buildingRestrictionsTimes(order);
  return times ? `Building restrictions: ${times}` : 'Building restrictions';
};

const buildingRestrictionsTimesId = (order: DispatcherOrderRecord) => {
  if (!buildingRestrictionsTimes(order)) {
    return undefined;
  }

  return `dispatcher-order-building-restriction-times-${order.id}`;
};

const whiteGloveTag = (order: DispatcherOrderRecord) => {
  const tags = (order.tags ?? []).filter(
    (tag) => tag.includes('Basic') || tag.includes('Packing Help') || tag.includes('Full Service'),
  );

  return tags[0];
};

const extendedServiceAreaTag = (order: DispatcherOrderRecord) => {
  const tags = (order.tags ?? []).filter((tag) => tag.includes('Service Area') && tag !== 'Extended Service Area');

  return tags[0];
};

const formatSlaWindow = (order: DispatcherOrderRecord, undispatched: boolean) => {
  const scheduledTime = new Date(order.scheduled);
  const scheduleTime = formatDateTz(scheduledTime.toISOString(), order.region.tz, 'h:mm', true).replace(/:00/g, '');
  const endTime = formatDateTz(
    new Date(
      scheduledTime.getTime() + (order.sla_window_size ?? 1) * DISPATCHER_CONSTANTS.DEFAULT_WINDOW_SLOT_DURATION * 1000,
    ).toISOString(),
    order.region.tz,
    'h:mm a',
    true,
  ).replace(/:00/g, '');

  if (undispatched) {
    return `SLA: ${scheduleTime}-${endTime}`;
  }

  const startTime = formatDateTz(
    new Date(scheduledTime.getTime() + (order.dispatch_offset ?? 0) * 1000).toISOString(),
    order.region.tz,
    'h:mm a',
    true,
  ).replace(/:00/g, '');

  return `SLA: ${scheduleTime}-${endTime} (est: ${startTime})`;
};

const customerName = (order: DispatcherOrderRecord) =>
  getPrimaryCustomer(order.account)?.name ?? order.account.customers[0]?.name ?? 'Unknown customer';

const estimationOrder = (order: DispatcherOrderRecord): RestOrderPayload => ({
  id: order.id,
  account_id: order.account_id ?? order.account.id,
  scheduled: order.scheduled,
  estimated_duration: order.estimated_duration,
  estimated_duration_overwritten_at: order.estimated_duration_overwritten_at ?? null,
  estimated_duration_override_reason: order.estimated_duration_override_reason ?? null,
  dispatch_offset: order.dispatch_offset,
  sla_window_size: order.sla_window_size,
  region: order.region,
  moving: order.moving,
  moving_operation: (order.moving_operation as RestOrderPayload['moving_operation'] | undefined) ?? null,
  type: order.type,
  subtype: order.subtype,
  service_type: order.service_type,
  address: {
    ...order.address,
    ...(order.address.id != null ? { id: String(order.address.id) } : {}),
  } as RestOrderPayload['address'],
});

export const DispatcherOrderPreview: React.FC<DispatcherOrderPreviewProps> = ({
  order,
  dispatch = null,
  mode = 'viewing',
  undispatched = false,
  onDragstart,
  onDragmove,
  onDragend,
}) => {
  const draggable = mode === 'modifying';
  const sizingClass = orderSizingClass(order, dispatch, undispatched);
  const useHoverOverlay = Boolean(dispatch) && !undispatched;
  const orderRef = useDispatcherOrderHoverOverlay(useHoverOverlay);
  const locationLabel = order.address.neighborhood || order.address.city || 'Unknown location';
  const dispatchError = orderDispatchError(order, dispatch, undispatched);
  const hasRescheduleError = dispatchError === 'rescheduled';
  const warehouseLabel = order.order_warehouses || order.account_warehouses;
  const restrictionTimes = buildingRestrictionsTimes(order);
  const restrictionTimesId = restrictionTimes ? buildingRestrictionsTimesId(order) : undefined;

  return (
    <div
      ref={orderRef}
      draggable={draggable}
      onDragOver={(event) => {
        if (draggable) {
          event.preventDefault();
        }
      }}
      onDragStart={(event) => {
        if (draggable) {
          event.dataTransfer.setData('text/plain', String(order.id));
          event.dataTransfer.effectAllowed = 'move';
          onDragstart?.(order);
        }
      }}
      onDrag={(event) => {
        if (draggable) {
          onDragmove?.(order, event.nativeEvent);
        }
      }}
      onDragEnd={() => {
        if (draggable) {
          onDragend?.(order);
        }
      }}
      className={[
        'dispatcher-order',
        orderTypeClass(order),
        order.subtype ? `dispatcher-order-${order.subtype}` : '',
        sizingClass,
        order.state ? `dispatcher-order-${order.state}` : '',
        order.account_warehouses ? 'dispatcher-order-has-warehouse' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={`dispatcher-order-content${hasRescheduleError ? ' dispatcher-error' : ''}`}>
        {whiteGloveTag(order) && <div className="label label-info">{whiteGloveTag(order)}</div>}
        {extendedServiceAreaTag(order) && <div className="label label-info">{extendedServiceAreaTag(order)}</div>}
        {order.long_distance_return && <div className="label label-info">Long-Distance</div>}
        {order.long_distance_move && <div className="label label-info">Long-Distance Move</div>}

        <div className="dispatcher-order-preview">
          <a target="_blank" rel="noreferrer" href={orderURL(order)}>
            <FontAwesomeIcon icon={order.type === 'return' ? faSignOut : faSignIn} />
          </a>
        </div>

        <div className="dispatcher-order-customer dispatcher-order-collapse text-overflow">
          <span>{customerName(order)}</span>
          {order.attached_service_display_name && (
            <>
              {' '}
              <strong style={{ color: '#60a2dd' }}>(Sim-Job {order.main_service_display_name})</strong>
            </>
          )}
          {!order.attached_service_display_name && order.main_service_display_name && (
            <>
              {' '}
              <strong>({order.main_service_display_name})</strong>
            </>
          )}
        </div>

        <div className="dispatcher-order-separator">-</div>
        <div className="dispatcher-order-address dispatcher-order-collapse text-overflow">{locationLabel}</div>

        {order.account_warehouses && (
          <>
            <div className="dispatcher-order-separator">-</div>
            <div className="dispatcher-order-warehouse dispatcher-order-collapse text-overflow">
              Warehouses: {warehouseLabel}
            </div>
          </>
        )}

        <div className="dispatcher-order-separator">-</div>
        <div className="dispatcher-order-scheduled dispatcher-order-collapse">
          {formatSlaWindow(order, undispatched)}
        </div>
        <div className="dispatcher-order-separator">-</div>

        <div className="dispatcher-order-estimated dispatcher-order-collapse">
          <DispatcherEstimationFormModal order={estimationOrder(order)} boardOrder={order} mode={mode} />
          {hasRescheduleError && <span className="label label-danger">rescheduled</span>}
          {order.multi_day && (
            <span className="label label-primary">
              Day {order.multi_day_order_position} of {order.multi_day_order_count}
            </span>
          )}
          {order.attached_service_display_name && (
            <span className="label label-primary">Attached {order.attached_service_display_name}</span>
          )}
          {!hasRescheduleError && order.state && (
            <span className={`label ${order.state === 'canceled' ? 'label-danger' : 'label-default'}`}>
              {order.state}
            </span>
          )}
        </div>

        <div className="dispatcher-order-movers dispatcher-order-collapse">
          {isWarehouseOrder(order) && (
            <Tooltip title="Warehouse order" placement={TooltipPlacement.Right}>
              <FontAwesomeIcon icon={faBuilding} />
            </Tooltip>
          )}
          {hasBuildingRestrictions(order) && (
            <Tooltip title={buildingRestrictionsTitle(order)} placement={TooltipPlacement.Right}>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                aria-label="Building restrictions"
                aria-describedby={restrictionTimesId}
                className="dispatcher-order-building-restrictions"
                role="img"
              />
            </Tooltip>
          )}
          {restrictionTimes && (
            <span id={restrictionTimesId} className="dispatcher-order-building-restriction-times">
              {restrictionTimes}
            </span>
          )}
          <DispatcherMoverCountIcon
            movers={order.movers ?? 1}
            overrideReason={order.mover_override_reason ?? undefined}
          />
        </div>
      </div>
    </div>
  );
};
